import {
  AUTH_NOTICE_QUERY_LOGOUT,
  AUTH_NOTICE_QUERY_PARAM,
} from '../constants/auth-notice'

export const AUTH_RETURN_PARAM = 'return'
export const AUTH_REFERER_PARAM = 'referer'

export const AUTH_LOGIN_PATH = '/ingresar'
export const AUTH_SIGNUP_PATH = '/registrate'

const AUTH_PATHNAMES = new Set([
  AUTH_LOGIN_PATH,
  AUTH_SIGNUP_PATH,
  '/close-sessions',
])

export function isAuthPathname (pathname = '') {
  if (!pathname) return false
  if (AUTH_PATHNAMES.has(pathname)) return true
  return pathname.startsWith(`${AUTH_SIGNUP_PATH}/`)
}

function normalizeReturnPath (path) {
  if (!path || typeof path !== 'string') return null

  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null

  return trimmed
}

/** Desanida returns que apuntan a /ingresar o /registrate. */
export function resolveReturnPath (path) {
  let current = normalizeReturnPath(path)
  let depth = 0

  while (current && depth < 10) {
    const queryIndex = current.indexOf('?')
    const pathname = queryIndex === -1 ? current : current.slice(0, queryIndex)

    if (!isAuthPathname(pathname)) {
      return current
    }

    if (queryIndex === -1) return null

    const inner = getReturnPathFromSearch(current.slice(queryIndex))
    if (!inner) return null

    current = normalizeReturnPath(inner)
    depth += 1
  }

  return null
}

/** Ruta completa interna: pathname + search + hash */
export function getFullReturnPath (locationOrPath) {
  if (typeof locationOrPath === 'string') {
    return resolveReturnPath(locationOrPath)
  }

  if (!locationOrPath) {
    const { pathname, search, hash } = window.location

    if (isAuthPathname(pathname)) {
      return getReturnPathFromSearch(search)
    }

    return resolveReturnPath(`${pathname}${search}${hash}`)
  }

  const { pathname = '', search = '', hash = '' } = locationOrPath

  if (isAuthPathname(pathname)) {
    return getReturnPathFromSearch(search)
  }

  return resolveReturnPath(`${pathname}${search}${hash}`)
}

/** Solo acepta rutas relativas internas (evita open redirect). */
export function sanitizeReturnPath (path) {
  return resolveReturnPath(path)
}

export function getReturnPathFromSearch (search = '') {
  const params = new URLSearchParams(search)
  const raw = params.get(AUTH_RETURN_PARAM)
  if (!raw) return null
  return resolveReturnPath(raw)
}

export function getRefererFromSearch (search = '') {
  const params = new URLSearchParams(search)
  return params.get(AUTH_REFERER_PARAM) || ''
}

/** Preserva `return` y `referer` al cambiar entre /ingresar y /registrate. */
export function buildAuthPageSearch (currentSearch = '', { returnPath } = {}) {
  const params = new URLSearchParams(currentSearch)
  const rawReturn = returnPath ?? params.get(AUTH_RETURN_PARAM)
  const resolvedReturn = rawReturn ? resolveReturnPath(rawReturn) : null

  const next = new URLSearchParams()

  if (resolvedReturn) {
    next.set(AUTH_RETURN_PARAM, resolvedReturn)
  }

  const referer = params.get(AUTH_REFERER_PARAM)
  if (referer) {
    next.set(AUTH_REFERER_PARAM, referer)
  }

  const qs = next.toString()
  return qs ? `?${qs}` : ''
}

export function buildLoginPath (returnPath) {
  const safeReturn = sanitizeReturnPath(returnPath)
  if (!safeReturn) return AUTH_LOGIN_PATH
  return `${AUTH_LOGIN_PATH}?${AUTH_RETURN_PARAM}=${encodeURIComponent(safeReturn)}`
}

export function buildSignupPath ({ returnPath, referer, currentSearch = '' } = {}) {
  const params = new URLSearchParams(currentSearch)
  const rawReturn = returnPath ?? params.get(AUTH_RETURN_PARAM)
  const safeReturn = rawReturn ? resolveReturnPath(rawReturn) : null

  const next = new URLSearchParams()

  if (safeReturn) {
    next.set(AUTH_RETURN_PARAM, safeReturn)
  }

  const safeReferer = referer || params.get(AUTH_REFERER_PARAM)
  if (safeReferer) {
    next.set(AUTH_REFERER_PARAM, safeReferer)
  }

  const qs = next.toString()
  return qs ? `${AUTH_SIGNUP_PATH}?${qs}` : AUTH_SIGNUP_PATH
}

export function buildCloseSessionsPath (returnPath) {
  const safeReturn = sanitizeReturnPath(returnPath)
  if (!safeReturn) return '/close-sessions'
  return `/close-sessions?${AUTH_RETURN_PARAM}=${encodeURIComponent(safeReturn)}`
}

export function resolvePostAuthPath (search = '') {
  return getReturnPathFromSearch(search) || '/'
}

/** Logout voluntario: sin `return`, solo aviso en login. */
export function buildLogoutLoginPath () {
  return `${AUTH_LOGIN_PATH}?${AUTH_NOTICE_QUERY_PARAM}=${AUTH_NOTICE_QUERY_LOGOUT}`
}

export function getAuthNoticeFromSearch (search = '') {
  const params = new URLSearchParams(search)
  const value = params.get(AUTH_NOTICE_QUERY_PARAM)
  if (value === AUTH_NOTICE_QUERY_LOGOUT) return AUTH_NOTICE_QUERY_LOGOUT
  return null
}

/** Quita `auth` de la query conservando return/referer. */
export function stripAuthNoticeFromSearch (search = '') {
  const params = new URLSearchParams(search)
  params.delete(AUTH_NOTICE_QUERY_PARAM)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}
